import type { PDFPageProxy, TextItem } from 'pdfjs-dist/types/src/display/api'

import { type PdfPageText, pdfTextLines } from './pdf-text-lines'
import { linesToHtml } from './text-layout'

const fontNameOf = (page: PDFPageProxy, fontId: string) => {
  try {
    return (page.commonObjs.get(fontId) as { name?: string } | null)?.name
  } catch {
    return undefined
  }
}

const readPage = async (page: PDFPageProxy): Promise<PdfPageText> => {
  // Loads the page's fonts, so their real names (and with them bold and
  // italic) are known - the text content only refers to them by pdf.js id.
  await page.getOperatorList()
  const content = await page.getTextContent()
  const items = content.items.filter((item): item is TextItem => 'str' in item)

  const fontNames = new Map<string, string>()
  new Set(items.map(item => item.fontName)).forEach(fontId => {
    const name = fontNameOf(page, fontId)
    if (name) fontNames.set(fontId, name)
  })
  return { items, fontNames }
}

/**
 * Reads the text of a PDF into editor HTML: paragraphs, headings by font size,
 * and bold and italic from the font names. pdf.js is loaded only when a PDF is
 * imported. Returns null when the PDF has no text layer (a scan).
 */
export async function pdfToHtml(file: File): Promise<string | null> {
  const [pdfjs, { default: workerSrc }] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ])
  pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

  const data = new Uint8Array(await file.arrayBuffer())
  const loading = pdfjs.getDocument({ data })
  const doc = await loading.promise

  try {
    const pages: PdfPageText[] = []
    for (let number = 1; number <= doc.numPages; number++)
      pages.push(await readPage(await doc.getPage(number)))

    return linesToHtml(pdfTextLines(pages))
  } finally {
    await loading.destroy()
  }
}
